import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EpeService from "../services/EpeService";

export const EpeAccessContext = React.createContext();

// A case manager can grant or remove EPE operator access at any time, so the case page reads it again.
const POLL_INTERVAL_MS = 15000;

const sameAccess = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The one EPE (JustCue) access poller of a case page, shared by the tab strip (the entry tab) and the case sheet
 * (operator line, case management buttons): it reads `GET epe/cases/:caseId/access` on mount, every 15 s while
 * the page is visible and when the window gets focus. A failed read keeps the last known access and is never
 * shown as a snackbar.
 */
function EpeAccessProvider({ caseId, enabled = true, children }) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Only the newest read may answer: an older one (overtaken, or for another case) is dropped.
  const requestSeq = useRef(0);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const active = !!enabled && !!caseId;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!active || !mounted.current) return null;
    const seq = ++requestSeq.current;
    inFlight.current = true;
    try {
      const res = await EpeService.getAccess(caseId);
      if (seq === requestSeq.current) {
        setAccess((prev) => (sameAccess(prev, res) ? prev : res || null));
        setError(null);
      }
      return res || null;
    } catch (err) {
      if (seq === requestSeq.current) setError(err);
      return null;
    } finally {
      if (seq === requestSeq.current) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, [active, caseId]);

  useEffect(() => {
    setAccess(null);
    setError(null);
    setLoading(active);
    if (!active) return undefined;

    refresh();
    const poll = () => {
      if (!inFlight.current && document.visibilityState === "visible") {
        refresh();
      }
    };
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    window.addEventListener("focus", poll);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", poll);
      requestSeq.current += 1;
      inFlight.current = false;
    };
  }, [active, refresh]);

  const value = useMemo(() => {
    return { access, loading, error, refresh };
  }, [access, loading, error, refresh]);

  return (
    <EpeAccessContext.Provider value={value}>
      {children}
    </EpeAccessContext.Provider>
  );
}

export default EpeAccessProvider;
